import { awaitRedraw, create, el } from "@dhx/ts-common/dom";
import { View } from "@dhx/ts-common/view";
import { SpreadsheetEvents } from "./types";
import { getCellIds } from "./helpers/main";
import {
	T_ARG,
	T_DATA,
	T_RANGE,
	T_OPERATOR,
	IFormula,
	Store,
	DataPage,
	fromId,
	T_PAGE,
	T_ERROR,
	T_SPACE
} from "@xbs/muon";
import EditPopup from "./EditPopup";
import { getStringWidth } from "@dhx/ts-common/core";
import { IGrid } from "@dhx/ts-grid";

export class EditLine extends View {
	private _htmlEvents: any;
	private _value: string;
	private _colored: string;
	private _locked: boolean;
	private _input: HTMLInputElement;
	private _prevStyles: { cell: string | null; styleNum: number }[];
	private _prevCursorPosition: number;
	private _isMath: IFormula;
	private _suggest: EditPopup;
	private _store: Store;
	private _presedEnter: boolean;
	private _activePage: DataPage;
	private _editMode: { page: DataPage; cell: string; pageName: string };
	private _isEdit: boolean;
	private _grid: IGrid;

	constructor(element?: string | HTMLElement, config: any = {}) {
		super(element, config);
		this._store = config.math;
		this._activePage = config.activePage;
		this._grid = config.grid;
		this._suggest = new EditPopup((x, s, e, c) => this.setAtCursor(x, s, e, c));

		this._prevStyles = [];
		this._htmlEvents = {
			oninput: e => {
				if(!this._isEdit) {
					e.target.value = this._value;
				}
				this._value = e.target.value;
				this._isMath =
					this._value && this._value.toString().startsWith("=") ? this._parse(this._value) : null;
				if (this._isMath) {
					this._suggest.show(this._value, e.target, this._isMath, this.config.spreadsheet._grid.config.height );
				}
				if (!this._editMode) {
					this.config.events.fire(SpreadsheetEvents.editLineInput, [e.target.value]);
				} else {
					this.setValue(this._value);
				}
			},
			onfocus: e => {
				if (!this._editMode) {
					this.config.events.fire(SpreadsheetEvents.editLineFocus, [e.target.value, e]);
				}
			},
			onblur: e => {
				if (!this._editMode) {
					this.config.events.fire(SpreadsheetEvents.editLineBlur, [e.target.value, e]);
				} else {
					this.focus();
				}
			},
			onclick: e => {
				const cursor = this._input.selectionStart;
				if (cursor !== this._prevCursorPosition && this._isMath) {
					this._suggest.show(this._value, e.target, this._isMath);
					this._prevCursorPosition = cursor;
				}
			},
			onselectionchange: () => {
				const view = this.getRootView();
				const wrapper = view && view.refs && view.refs.input_wrapper;
				let left = "0px";
				if (this._isEdit && this._value  && this._input) {
					const cursor = this._input.selectionStart;
					const valWidth = getStringWidth(`${this._value}`.substr(0, cursor)) + 12;
					const node = this.getRootNode();
					const nodeWidth = node.offsetWidth;
					
					if (wrapper && nodeWidth < valWidth) {
						left = nodeWidth - valWidth + "px";
					}
				}
				if (wrapper) {
					wrapper.el.style.left = left;
					this.paint();
				}
				return
			}
		};
		document.addEventListener("selectionchange", this._htmlEvents.onselectionchange)
		this.config.events.on(SpreadsheetEvents.cellInput, (_cell, val) => {
			this._setValue(val);
		});
		this.config.events.on(SpreadsheetEvents.afterSheetChange, () => {
			this._activePage = (this.config.spreadsheet as any)._activePage;
			const edit = (this.config.spreadsheet as any)._editMode;
			const cursor = this._input && this._input.selectionStart;
			if (edit) {
				this._editMode = edit;
				this._removeStyles();
				this._setValue(this._value);
				this.focus();
				this.setCursor(cursor);
				if (edit.pageName === this.config.spreadsheet.getActiveSheet().name) {
					this._editMode = null;
				}
			} else {
				this._editMode = null;
			}
		});

		this.config.events.on(SpreadsheetEvents.afterEditStart, () => {
			this._isEdit = true;
		});
		this.config.events.on(SpreadsheetEvents.afterEditEnd, () => {
			this._isEdit = false;
		});
		const render = () => this._render();
		this.mount(element, create({ render }));
	}
	_parse(text: string, ctx: any = this._activePage.getContext()) {
		return this._store.parse(text, ctx);
	}

	navigate(key: "arrowDown" | "arrowUp" | "enter") {
		if (key === "enter" && this._isMath) {
			this._presedEnter = true;
		}
		if (this._editMode && this._presedEnter) {
			this.setCellMath();
			return false;
		}
		return this._suggest.navigate(key);
	}

	setAtCursor(value: string, from: number, to: number, cursor: number) {
		if (!this._input) return
		const startStr = this._value.substr(0, from);
		const endStr = this._value.substr(to);
		const newVal = startStr + value + endStr;
		if (!this._editMode) {
			this.config.events.fire(SpreadsheetEvents.editLineInput, [newVal]);
		} else {
			this.setValue(newVal);
		}
		this._prevCursorPosition = from + cursor;
		this._input.setSelectionRange(from + cursor, from + cursor);
	}

	focus() {
		if (this._input) {
			this._input.focus();
		}
	}

	blur() {
		if (this._input) {
			this._input.blur();
		}
	}

	lock() {
		this._locked = true;
		this.paint();
	}

	unlock() {
		this._locked = false;
		this.paint();
	}

	setValueAtRedactor(val: string) {
		const cursor = this._input && this._input.selectionStart;
		this.setAtCursor(val, cursor, cursor, val.length);
	}

	setValue(val: string) {
		this.unlock();
		this._setValue(val);
	}

	_setValue(val: string) {
		this._value = val;
		this._isMath =
			this._value && this._value.toString().startsWith("=") ? this._parse(this._value) : null;
		this._colored = this._parseColors(val);
		if (this._input) this._input.value = val;
		this._presedEnter = false;
		this._htmlEvents.onselectionchange();
		this.paint();
	}

	setCellMath() {
		if (!this._editMode) return;
		this._removeStyles();
		const { row, col } = getCellIds(this._editMode.cell, this._grid);
		const page = this._editMode.page;
		this._editMode = null;
		page.setCell(row, col, { nextValue: this._value });
		(this.config.spreadsheet as any)._changeSheet(page.getMeta().toolbarId);
		this.blur();
		this._presedEnter = false;
		return;
	}

	getValue() {
		return this._value;
	}

	clean() {
		// [dirty]
		setTimeout(() => {
			this._setValue("");
		}, 1);
	}

	destructor() {
		this._suggest.destructor();
	}

	getInput() {
		return this._input;
	}

	setCursor(cursor: number) {
		this._input && this._input.setSelectionRange(cursor, cursor);
	}

	isCellExpected(cell: string, needToSet: boolean, editor: HTMLInputElement, editedCell?: string): number {
		if (this._presedEnter) {
			this._presedEnter = false;
			return;
		}
		if (this._isMath && this._isEdit) {
			const val = this._value;
			const comma = cell.lastIndexOf(",");
			const spliter = cell.lastIndexOf(":");
			const commaIndex = comma > spliter ? comma : spliter;
			const isSpliter = cell.includes(",") || cell.includes(":");

			const cursorStart = editor
				? editor.selectionStart
				: this._input
				? this._input.selectionStart
				: this._value.length;

			const code = this._isMath.code;

			// Trims  cell range from cell:cell or cell,cell  to :cell and ,cell when it was ctrl or shift editedCell
			if (commaIndex > -1 && cell.includes(editedCell)) {
				const parse = this._parse(this._isMath.source.substring(0, cursorStart)).code.reverse();
				const sumbolBeforeCursor = parse[2] === 12 ? parse[5] : parse[2];
				if (sumbolBeforeCursor !== T_OPERATOR) {
					cell = cell.substring(commaIndex);
				} else {
					cell = cell.substring(commaIndex + 1);
				}
			}
			for (let i = 0; i < code.length; i += 3) {
				// finds the char witch is before selection
				const isCursorAfter = code[i + 1] + code[i + 2] == cursorStart;
				const char = code[i] === T_SPACE ? code[i-3] : code[i];
				// checks if the char before selection is a math operator or if the incoming cell is a range
				if (
					isCursorAfter &&
					((char == T_OPERATOR && val[code[i + 1]] !== ")") || isSpliter)
				) {
					//set incoming cell to Edit input
					if (needToSet) {
						const active = this.config.spreadsheet.getActiveSheet().name;
						if (this._editMode && this._editMode.pageName !== active) {
							if (isSpliter) {
								cell = `${cell.includes(",") ? "," : ":"}'${active}'!${cell.substring(1)}`;
							} else {
								cell = `'${active}'!${cell}`;
							}
						}
						const startStr = val.substring(0, cursorStart);
						const endStr = val.substring(cursorStart);
						const newval = startStr + cell + endStr;
						if (!this._editMode) {
							this.config.events.fire(SpreadsheetEvents.editLineInput, [newval]);
						} else {
							this.setValue(newval);
							this.setCursor(startStr.length + cell.length);
						}
						return startStr.length + cell.length;
					}

					return cursorStart;
				}
			}
		}
		return 0;
	}

	private _render() {
		return el(".dhx_edit_line", {}, [
			el(".input__wrapper", [
				el(".input_value", {
					".innerHTML": this._colored,
					_ref: "input_wrapper",
				}),
				el("input.dhx_edit_line_input", {
					_hooks: {
						didInsert: n => {
							this._input = n.el;
						},
						didUnmount: () => {
							document.removeEventListener("selectionchange", this._htmlEvents.onselectionchange)
						}
					},
					oninput: this._htmlEvents.oninput,
					onfocus: this._htmlEvents.onfocus,
					onblur: this._htmlEvents.onblur,
					onclick: this._htmlEvents.onclick,
					value: this._value || "",
					_ref: "input",
					disabled: this._locked,
				}),
				el(".input-animation"),
			]),
		]);
	}

	private _parseColors(val) {
		this._removeStyles();
		let styleNum = 1;
		let result = val;
		if (val && val[0] === "=") {
			const code = this._parse(val).code;
			result = "";
			let valCursor = 0;

			for (let i = 0; i < code.length; i += 3) {
				switch (code[i]) {
					case T_SPACE : 
						for (let c = 0; c < code[i + 2]; c++) {
							result += `&nbsp;`;
							valCursor++
						}
						break;
					case T_ARG:
					case T_RANGE:
						// eslint-disable-next-line no-case-declarations
						const str = val.substr(code[i + 1], code[i + 2]);
						// eslint-disable-next-line no-case-declarations
						const isSelected = this._prevStyles.find(item => item && item.cell === str);

						styleNum = styleNum === 9 ? 1 : styleNum;
						if (code[i + 1] > valCursor) {
							result += val.substr(valCursor, code[i + 1] - valCursor);
						}
						valCursor = code[i + 1] + code[i + 2];
						result += `<span class=text_range_${
							isSelected ? isSelected.styleNum : styleNum
						}>${str}</span>`;

						if (!isSelected && code[i - 3] !== T_ERROR) {
							this._setStyles(str, styleNum);
							this._prevStyles.push({ cell: str, styleNum });
							++styleNum;
						}
						break;
					case T_DATA:
					case T_ERROR:
						break;
					case T_PAGE:
						if (code[i + 3] !== T_ARG && code[i + 3] !== T_RANGE && code[i + 3] !== T_ERROR) {
							if (code[i + 1] !== valCursor) {
								result += val.substr(valCursor, code[i + 1] - valCursor);
							}
							valCursor = code[i + 1] + code[i + 2];
							result += val.substr(code[i + 1], code[i + 2]);
						}
						break;
					default:
						if (code[i + 1] > valCursor) {
							result += val.substr(valCursor, code[i + 1] - valCursor);
						}
						valCursor = code[i + 1] + code[i + 2];
						result += val.substr(code[i + 1], code[i + 2]);
				}
			}
			if (valCursor !== val.length) {
				result += val.substr(valCursor, val.length - valCursor);
			}
		}

		return result;
	}

	private _setStyles(cell, i) {
		if (cell.includes("$")) {
			cell = cell = cell.split("$").join("");
		}
		if (!cell.includes("!")) {
			cell =
				`'${(this._editMode ? this._editMode.pageName : this.config.spreadsheet.getActiveSheet().name)}'!${cell}`;
		}
		const grid = this.config.grid;
		const code = this._parse(cell).code;

		if (fromId(code[7])[2] !== this._activePage.getID()) {
			return;
		} else if (cell.includes("!")) {
			cell = cell.substr(cell.indexOf("!") + 1);
		}
		if (cell.indexOf(":") !== -1) {
			this.config.spreadsheet.eachCell(c => {
				const { row, col } = getCellIds(c, this._grid);
				grid.addCellCss(`${row + 1}`, `${col + 1}`, `range_${i}`);
			}, cell);
		} else {
			const { row, col } = getCellIds(cell, this._grid);
			grid.addCellCss(`${row + 1}`, `${col + 1}`, `range_${i}`);
		}
	}

	private _removeStyles() {
		for (const item of this._prevStyles) {
			if (!item) return;
			const grid = this.config.grid;
			let cell = item.cell;
			if (cell.includes("$")) {
				cell = cell.split("$").join("");
			}
			if (cell.includes("!")) {
				cell = cell.substr(cell.indexOf("!") + 1);
			}
			if (cell.includes(":")) {
				this.config.spreadsheet.eachCell(c => {
					const { row, col } = getCellIds(c, this._grid);
					grid.removeCellCss(`${row + 1}`, `${col + 1}`, `range_${item.styleNum}`);
				}, cell);
			} else {
				const { row, col } = getCellIds(cell, this._grid);
				grid.removeCellCss(`${row + 1}`, `${col + 1}`, `range_${item.styleNum}`);
			}
		}

		this._prevStyles = [];
	}
}
